/**
 * ADR-0166 — Benchmarks view — "how you compare".
 *
 * Three-state renderer:
 *   1. Opted-out gate — kicker + hero + five privacy commitments + amber
 *      fair-exchange callout + "Opt in to benchmarks" CTA + "Read full
 *      methodology" button.
 *   2. Opted-in normal — OPTED IN pill + Opt out ghost button + cohort
 *      selector (INDUSTRY / VOLUME BAND / COHORT SIZE), amber "Prototype
 *      data." banner whenever `cohort_is_synthetic` (always true in v1),
 *      six-card grid, category breakdown (when cohort_size >= 30) or the
 *      muted amber "Category breakdown requires…" notice.
 *   3. Opted-in cohort-too-small (cohort_size < 10) — cohort selector
 *      (with a coral COHORT SIZE readout) + "COHORT TOO SMALL" card,
 *      no cards, no breakdown.
 *
 * Loading / error states render inline EmptyStateCard messages so the
 * merchant never sees a blank surface. Root element carries
 * `data-testid="benchmarks-view"` per the ADR §Test plan router-dispatch
 * assertion.
 *
 * Copy is word-for-word from `docs/reference/Foyer_Dashboard_CURRENT.jsx`
 * lines 6849–7136 with two edits per Sill voice: "Foyer" → "Sill"
 * (dashboard is Sill; ADR-0013) and the "24h advisory" toast wording
 * preserved verbatim.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Lock } from 'lucide-react';

import type {
  BenchmarkIndustry,
  BenchmarkVolumeBand,
  BenchmarksResponse,
  SiteId,
} from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import {
  Card,
  EmptyStateCard,
  NumLabel,
  Pill,
  Tooltip,
} from '../../components/atoms/index.js';
import { BenchmarkCard } from './BenchmarkCard.js';
import { BenchmarkMethodologyModal } from './BenchmarkMethodologyModal.js';
import {
  BENCHMARK_INDUSTRIES,
  BENCHMARK_VOLUME_BANDS,
} from './constants.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

/** Prototype k-anonymity floors — the server enforces the SAME floors
 *  (ADR-0166 §3); these constants only ever surface in the client-side
 *  fallback copy the view renders when the response has null cards /
 *  breakdown. Matched to the reference lines 6870–6871. */
const K_MIN_DISPLAY = 10;
const K_MIN_BREAKDOWN = 30;

/** Default selectors — match the prototype defaults + the server route
 *  (§1 wire contract): food_bev + mid. Fresh open lands here. */
const DEFAULT_INDUSTRY: BenchmarkIndustry = 'food_bev';
const DEFAULT_VOLUME_BAND: BenchmarkVolumeBand = 'mid';

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

interface PrivacyCommitment {
  readonly title: string;
  readonly body: string;
}

/** Opt-in commitments rendered on the gate. The real-aggregates pipeline
 *  has not shipped yet, so these describe the pipeline's design — they
 *  do not describe anything your opt-in enables today. The gate itself
 *  carries that caveat, and the Prototype banner is shown every time
 *  the view renders a cohort. (Pared back 2026-10-02 from the previous
 *  five-item list, which promised aggregation guarantees that no real
 *  cohort yet exists to apply them to.) */
const PRIVACY_COMMITMENTS: readonly PrivacyCommitment[] = [
  {
    title: 'Opting in records your preference',
    body: "Once a real cohort exists, your site will be included in the aggregates. Until then opt-in has no effect on your account or on this view.",
  },
  {
    title: 'No amounts, no PII will be shared',
    body: "When the real pipeline ships, Sill will not share dollar amounts, customer information, mandate details, or anything that could identify your site to other merchants.",
  },
  {
    title: 'k-anonymity floor is 10 merchants',
    body: "When real aggregates ship, cohorts with fewer than 10 merchants will show \"not enough data\" and category breakdowns will require at least 30.",
  },
  {
    title: 'Off in 24 hours',
    body: 'Opting out stops any future contribution within 24 hours.',
  },
];

export function Benchmarks(): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const { activeSite } = useSites();
  const siteId = activeSite?.site_id;

  const [industry, setIndustry] = useState<BenchmarkIndustry>(DEFAULT_INDUSTRY);
  const [volumeBand, setVolumeBand] =
    useState<BenchmarkVolumeBand>(DEFAULT_VOLUME_BAND);
  const [response, setResponse] = useState<BenchmarksResponse | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [mutating, setMutating] = useState<boolean>(false);
  const [methodologyOpen, setMethodologyOpen] = useState<boolean>(false);

  // Load / refresh the full benchmarks response for the current site +
  // selectors. Called on mount, on site change, and after any successful
  // opt-in / opt-out mutation (so the view reflects server-side truth,
  // never an optimistic guess).
  const loadBenchmarks = useCallback(
    async (
      site_id: SiteId,
      ind: BenchmarkIndustry,
      band: BenchmarkVolumeBand,
    ): Promise<void> => {
      setState('loading');
      const result = await api.benchmarks.getBenchmarks(site_id, ind, band);
      if (result.kind === 'ok') {
        setResponse(result.response);
        setState('loaded');
        return;
      }
      // not_found / error — surface a neutral error state; the view
      // renders an EmptyStateCard rather than a blank surface.
      setResponse(null);
      setState('error');
    },
    [api],
  );

  useEffect(() => {
    if (!siteId) {
      setState('idle');
      setResponse(null);
      return;
    }
    void loadBenchmarks(siteId as SiteId, industry, volumeBand);
  }, [siteId, industry, volumeBand, loadBenchmarks]);

  const handleOptIn = useCallback(async (): Promise<void> => {
    if (!siteId || mutating) return;
    setMutating(true);
    const result = await api.benchmarks.optIn(siteId as SiteId);
    setMutating(false);
    if (result.kind === 'ok') {
      toast({
        title: 'Opted in to benchmarks',
        body: 'Your data joins the cohort. Aggregates appear within 24 hours.',
        tone: 'lime',
      });
      // Refresh with the current selectors so the six-card grid renders
      // against the just-flipped state.
      void loadBenchmarks(siteId as SiteId, industry, volumeBand);
      return;
    }
    if (result.kind === 'forbidden') {
      toast({
        title: "You don't have permission to opt this site in.",
        tone: 'coral',
      });
      return;
    }
    if (result.kind === 'not_found') {
      toast({ title: 'Site not found. Refresh the page and try again.', tone: 'coral' });
      return;
    }
    toast({ title: "Couldn't opt in.", body: result.kind === 'error' ? result.message : 'Try again shortly.', tone: 'coral' });
  }, [api, industry, loadBenchmarks, mutating, siteId, toast, volumeBand]);

  const handleOptOut = useCallback(async (): Promise<void> => {
    if (!siteId || mutating) return;
    setMutating(true);
    const result = await api.benchmarks.optOut(siteId as SiteId);
    setMutating(false);
    if (result.kind === 'ok') {
      toast({
        title: 'Opted out',
        body: 'New data stops within 24 hours. Existing aggregates remain.',
        tone: 'amber',
      });
      void loadBenchmarks(siteId as SiteId, industry, volumeBand);
      return;
    }
    if (result.kind === 'forbidden') {
      toast({
        title: "You don't have permission to opt this site out.",
        tone: 'coral',
      });
      return;
    }
    if (result.kind === 'not_found') {
      toast({ title: 'Site not found. Refresh the page and try again.', tone: 'coral' });
      return;
    }
    toast({ title: "Couldn't opt out.", body: result.kind === 'error' ? result.message : 'Try again shortly.', tone: 'coral' });
  }, [api, industry, loadBenchmarks, mutating, siteId, toast, volumeBand]);

  // ---------------------------------------------------------------
  // Render.
  // ---------------------------------------------------------------

  if (!siteId) {
    return (
      <div
        data-testid="benchmarks-view"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
      >
        <Card>
          <EmptyStateCard
            label="No active site"
            hint="Pick a site to see how your policy compares."
          />
        </Card>
      </div>
    );
  }

  if (state === 'loading' || state === 'idle' || response === null) {
    if (state === 'error') {
      return (
        <div
          data-testid="benchmarks-view"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
        >
          <Card>
            <EmptyStateCard
              label="Benchmarks unavailable"
              hint="We couldn't load your benchmarks. Try again shortly."
            />
          </Card>
        </div>
      );
    }
    return (
      <div
        data-testid="benchmarks-view"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
      >
        <Card>
          <EmptyStateCard
            label="Loading benchmarks"
            hint="Computing how your policy compares…"
          />
        </Card>
      </div>
    );
  }

  // Opted-out gate — the merchant has not opted in. Show the five
  // privacy commitments + amber fair-exchange callout + CTA.
  if (response.opt_in_state === 'opted_out') {
    return (
      <div
        data-testid="benchmarks-view"
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
      >
        <div className="mb-8">
          <NumLabel n={6}>Benchmarks · how you compare</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 52px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              marginBottom: 16,
            }}
          >
            See how your policy stacks up <em>against your cohort</em>.
          </h1>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 16,
              color: C.muted,
              lineHeight: 1.6,
              maxWidth: 640,
            }}
          >
            Compare your rule coverage, block rates, and response times
            against an anonymized cohort of similar merchants. Opt in to
            participate and unlock the view.
          </p>
        </div>

        <Card className="p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={13} color={C.limeFg} />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.limeFg,
                letterSpacing: 2,
                fontWeight: 600,
              }}
            >
              PRIVACY COMMITMENTS
            </span>
          </div>
          <h2
            style={{
              fontFamily: serifFont,
              fontSize: 24,
              color: C.text,
              letterSpacing: -0.5,
              marginBottom: 16,
            }}
          >
            What you opt into.
          </h2>

          <div className="space-y-3 mb-6">
            {PRIVACY_COMMITMENTS.map((c) => (
              <div
                key={c.title}
                className="flex items-start gap-3 p-3"
                style={{
                  background: C.surface2,
                  border: `1px solid ${C.borderSoft}`,
                  borderRadius: 2,
                }}
              >
                <Check
                  size={13}
                  color={C.limeFg}
                  className="flex-shrink-0 mt-0.5"
                />
                <div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                    {c.title}
                  </div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.5,
                    }}
                  >
                    {c.body}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="p-3 mb-6"
            style={{
              background: C.amberDim,
              border: `1px solid ${C.amberFg}`,
              borderRadius: 2,
            }}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle
                size={13}
                color={C.amberFg}
                className="flex-shrink-0 mt-0.5"
              />
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.text,
                  lineHeight: 1.5,
                }}
              >
                <strong>This is an opt-in, two-way feature.</strong> If
                you opt in, your data contributes to others' benchmarks.
                If you opt out, you cannot see benchmarks either —
                fair-exchange model.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void handleOptIn()}
              disabled={mutating}
              className="flex items-center gap-2 px-5 py-2.5"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                cursor: mutating ? 'not-allowed' : 'pointer',
                opacity: mutating ? 0.55 : 1,
              }}
            >
              <Check size={14} /> Opt in to benchmarks
            </button>
            <button
              type="button"
              onClick={() => setMethodologyOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.muted,
                fontFamily: sansFont,
                fontWeight: 500,
                fontSize: 13,
                borderRadius: 2,
              }}
            >
              Read full methodology
            </button>
          </div>
        </Card>

        <BenchmarkMethodologyModal
          open={methodologyOpen}
          onClose={() => setMethodologyOpen(false)}
        />
      </div>
    );
  }

  // Opted-in. Cohort echo is populated whenever opt_in_state ===
  // 'opted_in' — even below the k-floor (the server sets it because the
  // selector-driven "COHORT TOO SMALL" copy needs the cohort_size to
  // render honestly).
  const cohort = response.cohort;
  const cohortSize = cohort?.cohort_size ?? 0;
  const cohortTooSmall = cohort?.cohort_too_small ?? true;

  return (
    <div
      data-testid="benchmarks-view"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
    >
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="min-w-0">
          <NumLabel n={6}>Benchmarks · how you compare</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 52px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
            }}
          >
            Anonymized peers, <em>same playing field</em>.
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <Tooltip content="You are opted in. Your data joins the cohort; you can see aggregates in return.">
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5"
              style={{
                background: C.limeDim,
                border: `1px solid ${C.limeFg}`,
                borderRadius: 2,
              }}
            >
              <Check size={11} color={C.limeFg} />
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.limeFg,
                  letterSpacing: 1.5,
                  fontWeight: 600,
                }}
              >
                OPTED IN
              </span>
            </div>
          </Tooltip>
          <button
            type="button"
            onClick={() => void handleOptOut()}
            disabled={mutating}
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              padding: '4px 10px',
              borderRadius: 2,
              cursor: mutating ? 'not-allowed' : 'pointer',
              opacity: mutating ? 0.55 : 1,
            }}
          >
            Opt out
          </button>
        </div>
      </div>

      {/* Cohort selector */}
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  marginBottom: 4,
                }}
              >
                INDUSTRY
              </div>
              <select
                value={industry}
                onChange={(e) =>
                  setIndustry(e.target.value as BenchmarkIndustry)
                }
                aria-label="Industry"
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.text,
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  padding: '6px 8px',
                  borderRadius: 2,
                }}
              >
                {BENCHMARK_INDUSTRIES.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  marginBottom: 4,
                }}
              >
                VOLUME BAND
              </div>
              <select
                value={volumeBand}
                onChange={(e) =>
                  setVolumeBand(e.target.value as BenchmarkVolumeBand)
                }
                aria-label="Volume band"
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.text,
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  padding: '6px 8px',
                  borderRadius: 2,
                }}
              >
                {BENCHMARK_VOLUME_BANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="text-right">
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                marginBottom: 4,
              }}
            >
              COHORT SIZE
            </div>
            <div
              data-testid="benchmarks-cohort-size"
              style={{
                fontFamily: monoFont,
                fontSize: 18,
                color: cohortTooSmall ? C.coralFg : C.text,
              }}
            >
              {cohortSize} merchants
            </div>
          </div>
        </div>
      </Card>

      {response.cohort_is_synthetic ? (
        <div
          data-testid="benchmarks-prototype-banner"
          className="p-3 mb-6 flex items-start gap-2 flex-wrap"
          style={{
            background: C.amberDim,
            border: `1px solid ${C.amberFg}`,
            borderRadius: 2,
          }}
        >
          <AlertTriangle
            size={13}
            color={C.amberFg}
            className="flex-shrink-0 mt-0.5"
          />
          <div
            className="flex-1 min-w-[260px]"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            <strong>Prototype data.</strong> Cohort percentiles below are
            synthetic placeholders. Your own values are read from your
            live policy, but cohort comparisons will reflect real
            aggregates once the production pipeline ships.
          </div>
        </div>
      ) : null}

      {cohortTooSmall ? (
        <Card className="p-8">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} color={C.coralFg} />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.coralFg,
                letterSpacing: 2,
                fontWeight: 600,
              }}
            >
              COHORT TOO SMALL
            </span>
          </div>
          <h2
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.4,
              marginBottom: 10,
            }}
          >
            Not enough data to compare yet.
          </h2>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.6,
              maxWidth: 540,
            }}
          >
            This cohort has {cohortSize} merchants. Sill requires at least{' '}
            {K_MIN_DISPLAY} merchants in a cohort before showing
            aggregates, so individual sites cannot be inferred. Try a
            broader industry or a different volume band.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {(response.cards ?? [])
              // ADR-0166 note — `mean_decision_ms` is permanently
              // `unavailable` on the wire today (no stored source for
              // decision latency). Rather than ship a card with a muted
              // "Not enough data yet" placeholder, suppress the row
              // entirely until the metric is sourced. The grid stays
              // tidy; the response is unchanged.
              .filter((card) => !(card.id === 'mean_decision_ms' && card.yours === null))
              .map((card) => (
                <BenchmarkCard key={card.id} card={card} />
              ))}
          </div>

          {response.category_breakdown ? (
            <Card className="p-6 mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      letterSpacing: 2,
                    }}
                  >
                    RULES BY CATEGORY
                  </div>
                  <h3
                    style={{
                      fontFamily: serifFont,
                      fontSize: 20,
                      color: C.text,
                      letterSpacing: -0.4,
                      marginTop: 4,
                    }}
                  >
                    Where the cohort spends rule budget.
                  </h3>
                </div>
                <Tooltip
                  content={`Cohort has ${cohortSize} merchants. Category breakdowns require k>=${K_MIN_BREAKDOWN}.`}
                >
                  <Pill tone="lime">k = {cohortSize}</Pill>
                </Tooltip>
              </div>
              <div className="space-y-4">
                {response.category_breakdown.map((row) => {
                  // Scale both bars against the max of this row plus a
                  // small headroom so neither bar ever fills 100%. Byte-
                  // preserved from the reference lines 7060–7061.
                  const maxVal =
                    Math.max(row.yours, row.cohort_median, 1) * 1.15;
                  const yoursPct = (row.yours / maxVal) * 100;
                  const medianPct = (row.cohort_median / maxVal) * 100;
                  const above = row.yours >= row.cohort_median;
                  return (
                    <div key={row.category}>
                      <div className="flex items-center justify-between mb-2">
                        <span
                          style={{
                            fontFamily: sansFont,
                            fontSize: 12,
                            color: C.text,
                          }}
                        >
                          {row.label}
                        </span>
                        <span
                          style={{
                            fontFamily: monoFont,
                            fontSize: 10,
                            color: C.muted,
                            letterSpacing: 0.5,
                          }}
                        >
                          you {row.yours} · median {row.cohort_median}
                        </span>
                      </div>

                      {/* Yours bar */}
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          style={{
                            fontFamily: monoFont,
                            fontSize: 9,
                            color: C.dim,
                            letterSpacing: 1,
                            width: 36,
                            flexShrink: 0,
                          }}
                        >
                          YOU
                        </span>
                        <div
                          className="relative flex-1"
                          style={{
                            height: 6,
                            background: C.surface2,
                            borderRadius: 2,
                          }}
                        >
                          {row.yours === 0 ? (
                            // Deliberate-zero tick: a zero-width fill reads
                            // as a rendering failure on mobile; a 3px stub
                            // at the axis start says "you have none".
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 3,
                                background: C.dim,
                                borderRadius: 2,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${yoursPct}%`,
                                background: above ? C.limeFg : C.amberFg,
                                borderRadius: 2,
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {/* Median bar */}
                      <div className="flex items-center gap-2">
                        <span
                          style={{
                            fontFamily: monoFont,
                            fontSize: 9,
                            color: C.dim,
                            letterSpacing: 1,
                            width: 36,
                            flexShrink: 0,
                          }}
                        >
                          MED
                        </span>
                        <div
                          className="relative flex-1"
                          style={{
                            height: 6,
                            background: C.surface2,
                            borderRadius: 2,
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              // C.muted, not C.border — the border gray is
                              // near-invisible against surface2 on dark
                              // OLED (founder-reported, 2026-07-07).
                              width: `${medianPct}%`,
                              background: C.muted,
                              borderRadius: 2,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : (
            <Card className="p-5 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle size={13} color={C.amberFg} />
                <div
                  data-testid="benchmarks-breakdown-notice"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.5,
                  }}
                >
                  Category breakdown requires at least {K_MIN_BREAKDOWN}{' '}
                  merchants in the cohort. This cohort has {cohortSize}.
                </div>
              </div>
            </Card>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3 mt-8">
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.dim,
                letterSpacing: 1.5,
                lineHeight: 1.5,
              }}
            >
              PROTOTYPE DATA · YOUR VALUES READ FROM YOUR LIVE POLICY ·
              COHORT PERCENTILES ARE SYNTHETIC PLACEHOLDERS
            </div>
            <button
              type="button"
              onClick={() => setMethodologyOpen(true)}
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                padding: '6px 12px',
                borderRadius: 2,
              }}
            >
              How we calculate these →
            </button>
          </div>
        </>
      )}

      <BenchmarkMethodologyModal
        open={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
      />
    </div>
  );
}

